import axios, { AxiosResponse } from 'axios';
import API_CONFIG from '../config/api.config';
import { secureStorage } from './SecureStorageService';

const API_BASE_URL = API_CONFIG.BASE_URL;

// Types
export interface User {
  id: string;
  username: string;
  email: string;
  photoUrl?: string;
  googleId?: string;
  isActive: boolean;
  lastLoginAt?: string;
  createdAt?: string;
}

export interface Device {
  id: string;
  name: string;
  deviceType: string;
  isActive: boolean;
  userId: string;
  osVersion?: string;
  appVersion?: string;
  lastSeenAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface FileInfo {
  url: string;
  type: string;
  extension: string;
  size: number;
  name: string;
}

export interface Message {
  id?: string; // Message ID (for mark-delete endpoint)
  contentId: string;
  type: 'text' | 'image' | 'files';
  text?: string;
  imageUrl?: string; // For backward compatibility with single image messages
  files?: FileInfo[]; // Array of files for multiple file messages
  timestamp: number;
  fromDeviceId?: string;
  fromDeviceName?: string;
  fromUserName?: string;
  isDelivered: boolean;
  deliveredAt?: string;
  isRead: boolean;
  readAt?: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
}

export interface AuthResponse {
  user: User;
  deviceId: string;
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface RegisterRequest {
  googleToken: string;
  deviceId: string;
  deviceName?: string;
}

export interface LoginRequest {
  googleToken: string;
  deviceId: string;
  deviceName?: string;
}

export interface LogoutRequest {
  accessToken: string;
}

export interface RefreshTokenRequest {
  refreshToken: string;
}

export interface RefreshTokenResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface FileData {
  data: string; // base64 data
  type: string; // MIME type
  extension?: string; // file extension
  name?: string; // file name
}

export interface ClipboardContentRequest {
  type: 'text' | 'image' | 'files';
  text?: string;
  imageData?: string; // base64 for single image (backward compatibility)
  files?: FileData[]; // array of files for multiple file messages
  targetDeviceId: string;
  sessionId?: string; // Session ID for continuing file uploads
}

export interface SessionResponse {
  sessionId: string;
  messageId: string;
  totalFiles: number;
  totalSize: number;
  files: FileInfo[];
}

export interface FinalizeSessionRequest {
  sessionId: string;
}

export interface FinalizeSessionResponse {
  sessionId: string;
  messageId: string;
  sent: boolean;
  timestamp: number;
}

// External message types
export interface ExternalMessageSessionResponse {
  sessionId: string;
  code?: string; // Only present for text messages
  messageId?: string;
  totalFiles: number;
  totalSize: number;
  files?: FileInfo[];
}

export interface ExternalMessageFinalizeResponse {
  sessionId: string;
  code: string;
  messageId: string;
  expiresAt: number;
}

export interface ExternalMessageReceiveResponse {
  id: string; // Message ID from backend
  sessionId?: string;
  type: string;
  text?: string;
  files?: FileInfo[];
  fromDeviceId?: string;
  fromDeviceName?: string;
  fromUserId?: string;
  fromUserName?: string;
  fromUserPhotoUrl?: string;
  timestamp?: number;
  receivedAt?: number;
}

// API Client
class ApiClient {
  private baseURL: string;
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private tokenExpirationHandler: (() => void) | null = null;
  private isRefreshing = false;
  private refreshPromise: Promise<string> | null = null;
  private refreshTimer: NodeJS.Timeout | null = null;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
    // Use secure storage service (sessionStorage for tokens)
    this.accessToken = secureStorage.getAccessToken();
    this.refreshToken = secureStorage.getRefreshToken();
    
    // Set up axios interceptor for automatic token refresh
    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor to add auth header
    axios.interceptors.request.use(
      (config) => {
        if (this.accessToken) {
          config.headers.Authorization = `Bearer ${this.accessToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor to handle token refresh
    axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            await this.refreshAccessToken();
            // Retry the original request with new token
            originalRequest.headers.Authorization = `Bearer ${this.accessToken}`;
            return axios(originalRequest);
          } catch (refreshError) {
            console.error('Token refresh failed:', refreshError);
            if (this.tokenExpirationHandler) {
              this.tokenExpirationHandler();
            }
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  setTokenExpirationHandler(handler: () => void) {
    this.tokenExpirationHandler = handler;
  }

  setAccessToken(token: string | null, forceResetExpiration: boolean = false) {
    this.accessToken = token;
    secureStorage.setAccessToken(token, forceResetExpiration);
  }

  setRefreshToken(token: string | null, forceResetExpiration: boolean = false) {
    this.refreshToken = token;
    secureStorage.setRefreshToken(token, forceResetExpiration);
  }

  setTokens(accessToken: string | null, refreshToken: string | null, forceResetExpiration: boolean = false) {
    this.setAccessToken(accessToken, forceResetExpiration);
    this.setRefreshToken(refreshToken, forceResetExpiration);
  }

  // Start automatic token refresh (every 45 minutes)
  startTokenRefresh() {
    // Clear existing timer if any
    this.stopTokenRefresh();
    
    // Refresh every 45 minutes (JWT tokens expire after 1 hour)
    this.refreshTimer = setInterval(async () => {
      try {
        if (this.refreshToken) {
          await this.refreshAccessToken();
          console.log('Automatic token refresh successful');
        } else {
          console.warn('No refresh token available for automatic refresh, stopping timer');
          this.stopTokenRefresh();
          if (this.tokenExpirationHandler) {
            this.tokenExpirationHandler();
          }
        }
      } catch (error) {
        console.error('Automatic token refresh failed:', error);
        // If refresh fails, clear tokens and trigger logout
        this.setTokens(null, null);
        this.stopTokenRefresh();
        if (this.tokenExpirationHandler) {
          this.tokenExpirationHandler();
        }
      }
    }, 45 * 60 * 1000); // 45 minutes
    
    console.log('Token refresh timer started (every 45 minutes)');
  }

  // Stop automatic token refresh
  stopTokenRefresh() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
      console.log('Token refresh timer stopped');
    }
  }

  private getHeaders() {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json; charset=utf-8',
    };

    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }

    return headers;
  }


  // Refresh access token using refresh token
  async refreshAccessToken(): Promise<string> {
    if (!this.refreshToken) {
      console.warn('No refresh token available, triggering logout');
      // Clear any remaining tokens
      this.setTokens(null, null);
      // Trigger logout flow
      if (this.tokenExpirationHandler) {
        this.tokenExpirationHandler();
      }
      throw new Error('Session expired. Please sign in again.');
    }

    // If already refreshing, return the existing promise
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    this.isRefreshing = true;
    this.refreshPromise = this.performTokenRefresh();

    try {
      const newAccessToken = await this.refreshPromise;
      return newAccessToken;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  private async performTokenRefresh(): Promise<string> {
    try {
      console.log('Refreshing access token...');
      
      const response: AxiosResponse<ApiResponse<RefreshTokenResponse>> = await axios.post(
        `${this.baseURL}/auth/refresh`,
        { refreshToken: this.refreshToken },
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (response.data.success && response.data.data) {
        const { accessToken, refreshToken } = response.data.data;
        
        // Update stored tokens
        this.setTokens(accessToken, refreshToken);
        
        console.log('Access token refreshed successfully');
        return accessToken;
      }

      throw new Error(response.data.message || 'Token refresh failed');
    } catch (error: any) {
      console.error('Token refresh failed:', error);
      
      // Check if refresh token is expired (401 or 400 response)
      const isExpiredRefreshToken = error.response?.status === 401 || 
                                    error.response?.status === 400 ||
                                    error.message?.includes('expired') ||
                                    error.message?.includes('invalid');
      
      // Clear tokens on refresh failure
      this.setTokens(null, null);
      
      // If refresh token is expired, trigger expiration handler to show login
      if (isExpiredRefreshToken && this.tokenExpirationHandler) {
        console.warn('Refresh token expired, triggering re-login');
        this.tokenExpirationHandler();
      }
      
      throw error;
    }
  }

  // Auth endpoints
  async register(request: RegisterRequest): Promise<AuthResponse> {
    const response: AxiosResponse<ApiResponse<AuthResponse>> = await axios.post(
      `${this.baseURL}/auth/register`,
      request,
      { headers: this.getHeaders() }
    );
    
    if (response.data.success && response.data.data) {
      const { accessToken, refreshToken } = response.data.data;
      // Force reset expiration on initial registration (7 days from now)
      this.setTokens(accessToken, refreshToken, true);
      return response.data.data;
    }
    
    throw new Error(response.data.message || 'Registration failed');
  }

  async login(request: LoginRequest): Promise<AuthResponse> {
    const response: AxiosResponse<ApiResponse<AuthResponse>> = await axios.post(
      `${this.baseURL}/auth/login`,
      request,
      { headers: this.getHeaders() }
    );
    
    if (response.data.success && response.data.data) {
      const { accessToken, refreshToken } = response.data.data;
      // Force reset expiration on initial login (7 days from now)
      this.setTokens(accessToken, refreshToken, true);
      return response.data.data;
    }
    
    throw new Error(response.data.message || 'Login failed');
  }

  async logout(): Promise<void> {
    if (!this.accessToken) return;

    const request: LogoutRequest = { accessToken: this.accessToken };

    try {
      await axios.post(
        `${this.baseURL}/auth/logout`,
        request,
        { headers: this.getHeaders() }
      );
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      this.setTokens(null, null);
    }
  }

  // Device endpoints
  async getDevices(): Promise<Device[]> {
    const response: AxiosResponse<ApiResponse<Device[]>> = await axios.get(
      `${this.baseURL}/devices`,
      { headers: this.getHeaders() }
    );
    
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    
    throw new Error(response.data.message || 'Failed to fetch devices');
  }

  async updateDevice(deviceId: string, name: string): Promise<Device> {
    try {
      const response: AxiosResponse<ApiResponse<Device>> = await axios.put(
        `${this.baseURL}/devices/${deviceId}`,
        { name },
        { headers: this.getHeaders() }
      );
      
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      
      throw new Error(response.data.message || 'Failed to update device');
    } catch (error: any) {
      // Extract error message from axios error response
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      // Fallback to generic error message
      throw new Error(error.message || 'Failed to update device');
    }
  }

  async deleteDevice(deviceId: string): Promise<void> {
    const response: AxiosResponse<ApiResponse<void>> = await axios.delete(
      `${this.baseURL}/devices/${deviceId}`,
      { headers: this.getHeaders() }
    );
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete device');
    }
  }

  // Message endpoints
  async getDeviceContent(deviceId: string, deleteAfterRead: boolean = false): Promise<Message[]> {
    const response: AxiosResponse<ApiResponse<Message[]>> = await axios.get(
      `${this.baseURL}/devices/${deviceId}/content`,
      { 
        headers: this.getHeaders(),
        params: { deleteAfterRead }
      }
    );
    
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    
    throw new Error(response.data.message || 'Failed to fetch messages');
  }

  async sendContent(request: ClipboardContentRequest): Promise<SessionResponse | void> {
    const response: AxiosResponse<ApiResponse<SessionResponse | void>> = await axios.post(
      `${this.baseURL}/content`,
      request,
      { headers: this.getHeaders() }
    );
    
    if (!response.data.success) {
      const errorMsg = response.data.message || 'Failed to send message';
      // Parse specific error messages for better UX
      if (errorMsg.includes('Maximum') && errorMsg.includes('files allowed')) {
        throw new Error(errorMsg);
      }
      if (errorMsg.includes('exceeds limit') || errorMsg.includes('size exceeds')) {
        throw new Error(errorMsg);
      }
      if (errorMsg.includes('Session expired') || errorMsg.includes('Session not found')) {
        throw new Error(errorMsg);
      }
      throw new Error(errorMsg);
    }
    
    // Return session data for files type, void for text
    return response.data.data;
  }

  async finalizeSession(sessionId: string): Promise<FinalizeSessionResponse> {
    const response: AxiosResponse<ApiResponse<FinalizeSessionResponse>> = await axios.post(
      `${this.baseURL}/content/finalize`,
      { sessionId },
      { headers: this.getHeaders() }
    );
    
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to finalize session');
    }
    
    return response.data.data;
  }

  async getSession(sessionId: string): Promise<SessionResponse> {
    const response: AxiosResponse<ApiResponse<SessionResponse>> = await axios.get(
      `${this.baseURL}/content/session/${sessionId}`,
      { headers: this.getHeaders() }
    );
    
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to get session');
    }
    
    return response.data.data;
  }

  async removeFilesFromSession(sessionId: string, fileUrls: string[]): Promise<SessionResponse> {
    const response: AxiosResponse<ApiResponse<SessionResponse>> = await axios.post(
      `${this.baseURL}/content/session/${sessionId}/files/remove`,
      { fileUrls },
      { headers: this.getHeaders() }
    );
    
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to remove files');
    }
    
    return response.data.data;
  }

  async deleteMessage(messageId: string): Promise<void> {
    const response: AxiosResponse<ApiResponse<void>> = await axios.delete(
      `${this.baseURL}/messages/${messageId}`,
      { headers: this.getHeaders() }
    );
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete message');
    }
  }

  async markMessageForDeletion(messageId: string): Promise<void> {
    const response: AxiosResponse<ApiResponse<void>> = await axios.post(
      `${this.baseURL}/content/${messageId}/mark-delete`,
      {},
      { headers: this.getHeaders() }
    );
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to mark message for deletion');
    }
  }

  // External message endpoints
  async createExternalMessageSession(type: 'text' | 'files', text?: string, files?: FileData[]): Promise<ExternalMessageSessionResponse> {
    const response: AxiosResponse<ApiResponse<ExternalMessageSessionResponse>> = await axios.post(
      `${this.baseURL}/external-messages/session`,
      { type, text, files },
      { headers: this.getHeaders() }
    );
    
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to create external message session');
    }
    
    return response.data.data;
  }

  async addFilesToExternalSession(sessionId: string, files: FileData[]): Promise<ExternalMessageSessionResponse> {
    const response: AxiosResponse<ApiResponse<ExternalMessageSessionResponse>> = await axios.post(
      `${this.baseURL}/external-messages/session/${sessionId}/files`,
      { files },
      { headers: this.getHeaders() }
    );
    
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to add files to external session');
    }
    
    return response.data.data;
  }

  async finalizeExternalSession(sessionId: string): Promise<ExternalMessageFinalizeResponse> {
    const response: AxiosResponse<ApiResponse<ExternalMessageFinalizeResponse>> = await axios.post(
      `${this.baseURL}/external-messages/session/${sessionId}/finalize`,
      {},
      { headers: this.getHeaders() }
    );
    
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to finalize external session');
    }
    
    return response.data.data;
  }

  async getExternalSession(sessionId: string): Promise<ExternalMessageSessionResponse> {
    const response: AxiosResponse<ApiResponse<ExternalMessageSessionResponse>> = await axios.get(
      `${this.baseURL}/external-messages/session/${sessionId}`,
      { headers: this.getHeaders() }
    );
    
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to get external session');
    }
    
    return response.data.data;
  }

  async removeFilesFromExternalSession(sessionId: string, fileUrls: string[]): Promise<ExternalMessageSessionResponse> {
    const response: AxiosResponse<ApiResponse<ExternalMessageSessionResponse>> = await axios.post(
      `${this.baseURL}/external-messages/session/${sessionId}/files/remove`,
      { fileUrls },
      { headers: this.getHeaders() }
    );
    
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to remove files from external session');
    }
    
    return response.data.data;
  }

  async receiveExternalMessage(code: string): Promise<ExternalMessageReceiveResponse> {
    try {
      const response: AxiosResponse<ApiResponse<ExternalMessageReceiveResponse>> = await axios.post(
        `${this.baseURL}/external-messages/receive`,
        { code: code.trim() },
        { headers: this.getHeaders() }
      );
      
      if (!response.data.success || !response.data.data) {
        // Extract detailed error message from response
        const errorMessage = response.data.message || 'Failed to receive external message';
        throw new Error(errorMessage);
      }
      
      return response.data.data;
    } catch (error: any) {
      // Extract detailed error message from axios error response
      if (error.response?.data?.message) {
        // Backend returned a specific error message
        throw new Error(error.response.data.message);
      } else if (error.response?.data?.error?.message) {
        // Some error formats have nested error.message
        throw new Error(error.response.data.error.message);
      } else if (error.message) {
        // Use the error message from the catch block
        throw new Error(error.message);
      } else {
        // Fallback to generic error
        throw new Error('Failed to receive external message. Please check the code and try again.');
      }
    }
  }

  async deleteExternalMessage(messageId: string): Promise<void> {
    const response: AxiosResponse<ApiResponse<void>> = await axios.delete(
      `${this.baseURL}/external-messages/${messageId}`,
      { headers: this.getHeaders() }
    );
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete external message');
    }
  }

  // Health check
  async healthCheck(): Promise<boolean> {
    try {
      const response = await axios.get(`${this.baseURL}/health`);
      return response.status === 200;
    } catch {
      return false;
    }
  }

  // Validate current token
  async validateToken(): Promise<boolean> {
    if (!this.accessToken) return false;
    
    try {
      // Try to make an authenticated request to validate the token
      await this.getDevices();
      return true;
    } catch (error: any) {
      // If the request fails with 401, the token is invalid
      if (error.response?.status === 401) {
        this.setAccessToken(null);
        return false;
      }
      // For other errors, assume token is still valid
      return true;
    }
  }
}

export const apiClient = new ApiClient(API_BASE_URL);
